/**
 * Rückmeldung aus der laufenden Anwendung.
 *
 * Sie wird ein Issue im Repository des Projekts — dort wird ohnehin
 * entschieden, was gebaut wird, und eine zweite Liste wäre eine, die niemand
 * liest.
 *
 * `html2canvas-pro` wird erst beim Öffnen des Dialogs nachgeladen. Im
 * Hauptbündel reiste es sonst bei jedem Seitenaufruf mit, obwohl es die
 * wenigsten je brauchen.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AlertTriangle, Camera, Check, Loader2, MessageSquarePlus, Send, X } from "lucide-react";
import { api } from "@/api.ts";

const ARTEN = [
  { wert: "fehler", beschriftung: "Fehler" },
  { wert: "wunsch", beschriftung: "Wunsch" },
  { wert: "frage", beschriftung: "Frage" },
] as const;

type Art = (typeof ARTEN)[number]["wert"];

async function ansichtAufnehmen(): Promise<string> {
  const { default: html2canvas } = await import("html2canvas-pro");
  const leinwand = await html2canvas(document.body, {
    logging: false,
    // Halbe Auflösung genügt, um zu erkennen, wovon die Rede ist — und hält
    // den Anhang klein genug für Gitea.
    scale: Math.min(window.devicePixelRatio, 1),
    useCORS: true,
  });
  return leinwand.toDataURL("image/jpeg", 0.7);
}

export function Rueckmeldung() {
  const ort = useLocation();
  const [offen, setOffen] = useState(false);
  const [art, setArt] = useState<Art>("fehler");
  const [text, setText] = useState("");
  const [bild, setBild] = useState("");
  const [bildMitschicken, setBildMitschicken] = useState(true);
  const [aufnehmend, setAufnehmend] = useState(false);
  const [sendend, setSendend] = useState(false);
  const [fehler, setFehler] = useState("");
  const [erfolg, setErfolg] = useState<{ nummer: number; url: string } | null>(null);
  const feldRef = useRef<HTMLTextAreaElement>(null);

  const oeffnen = useCallback(async () => {
    setFehler("");
    setErfolg(null);
    // Erst aufnehmen, dann den Dialog zeigen — sonst steht im Bild vor allem
    // der Dialog, und was der Melder eigentlich zeigen wollte, verdeckt er.
    setAufnehmend(true);
    try {
      setBild(await ansichtAufnehmen());
    } catch {
      setBild("");
    } finally {
      setAufnehmend(false);
    }
    setOffen(true);
    setTimeout(() => feldRef.current?.focus(), 50);
  }, []);

  const schliessen = useCallback(() => {
    setOffen(false);
    setText("");
    setBild("");
    setBildMitschicken(true);
  }, []);

  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => e.key === "Escape" && schliessen();
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [offen, schliessen]);

  async function absenden() {
    setSendend(true);
    setFehler("");
    try {
      const antwort = await api.rueckmeldung({
        art,
        nachricht: text,
        seite: ort.pathname + ort.search,
        fenster: `${window.innerWidth}×${window.innerHeight}`,
        browser: navigator.userAgent,
        oberflaeche: `${__RIBBON_COMMIT__} (gebaut ${__RIBBON_GEBAUT__})`,
        screenshot: bildMitschicken && bild ? bild : null,
      });
      setErfolg(antwort);
      setText("");
    } catch (e) {
      // Kein grüner Haken für etwas, das niemand je sieht.
      setFehler(e instanceof Error ? e.message : "Unbekannter Fehler");
    } finally {
      setSendend(false);
    }
  }

  return (
    <>
      <button
        onClick={oeffnen}
        disabled={aufnehmend}
        className="mx-2 mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-sidebar-hover disabled:opacity-60"
        title="Fehler melden, etwas wünschen oder fragen"
      >
        {aufnehmend ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <MessageSquarePlus size={18} />
        )}
        {aufnehmend ? "Moment …" : "Rückmeldung"}
      </button>

      {/*
        Der Dialog hängt am body, nicht hier im Baum. Die Navigation animiert
        ihr Ein- und Ausfahren über `transform`, und ein transformierter
        Vorfahre wird zum Bezugsrahmen für `position: fixed` — der Dialog säße
        sonst in der 240 Pixel schmalen Spalte statt über der Seite.
      */}
      {offen && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={schliessen}
        >
          <div
            className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-surface p-5 text-text shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Rückmeldung</h2>
              <button onClick={schliessen} className="rounded p-1 hover:bg-page-bg" aria-label="Schließen">
                <X size={18} />
              </button>
            </div>

            {erfolg ? (
              <div className="mt-4 space-y-3">
                <p className="flex items-start gap-2 text-sm">
                  <Check size={18} className="mt-0.5 shrink-0 text-success" />
                  <span>
                    Angelegt als Issue&nbsp;
                    <a href={erfolg.url} target="_blank" rel="noreferrer" className="text-primary underline">
                      #{erfolg.nummer}
                    </a>
                    .
                  </span>
                </p>
                <button
                  onClick={schliessen}
                  className="w-full rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary-dark"
                >
                  Schließen
                </button>
              </div>
            ) : (
              <>
                <div className="mt-4 flex gap-2">
                  {ARTEN.map((a) => (
                    <button
                      key={a.wert}
                      onClick={() => setArt(a.wert)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                        art === a.wert
                          ? "border-primary bg-primary text-white"
                          : "border-border hover:bg-page-bg"
                      }`}
                    >
                      {a.beschriftung}
                    </button>
                  ))}
                </div>

                <textarea
                  ref={feldRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={5}
                  placeholder="Was ist passiert? Was hatten Sie erwartet?"
                  className="mt-3 w-full rounded-lg border border-border bg-surface p-3 text-sm outline-none focus:border-primary"
                />

                {bild && (
                  <div className="mt-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={bildMitschicken}
                        onChange={(e) => setBildMitschicken(e.target.checked)}
                      />
                      <Camera size={16} />
                      Bildschirmfoto mitschicken
                    </label>
                    {bildMitschicken && (
                      <img
                        src={bild}
                        alt="Vorschau des Bildschirmfotos"
                        className="mt-2 max-h-40 w-full rounded-lg border border-border object-contain object-top"
                      />
                    )}
                    <p className="mt-1 text-xs text-text-muted">
                      Das Bild zeigt, was gerade auf dem Schirm stand — auch Orte und Zeiten.
                    </p>
                  </div>
                )}

                {fehler && (
                  <p className="mt-3 flex items-start gap-2 rounded-lg bg-danger/10 p-3 text-sm text-danger">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    {fehler}
                  </p>
                )}

                <button
                  onClick={absenden}
                  disabled={sendend || (!text.trim() && !(bildMitschicken && bild))}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
                >
                  {sendend ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Absenden
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
